import { Link } from "react-router-dom";
import { CreditPacks } from "@/components/plan/CreditPacks";
import { UsageChart } from "@/components/plan/UsageChart";
import { useUsage } from "@/hooks/queries";
import { formatDateTime, formatNumber } from "@/lib/format";

const OUTCOME_LABEL = { complete: "Answered", stopped: "Stopped", interrupted: "Interrupted", failed: "Failed" };
const KIND_LABEL = { message: "Chat", retry: "Retry", task_parse: "Tasks" };
const mediaLabel = (tier) => (tier === "video" ? "Video" : tier?.startsWith("image_edit") ? "Image edit" : "Image");
const activityLabel = (r) => (r.capability === "research" ? (r.tier === "strong" ? "Deep Search" : "Fast Search") : r.capability === "media" ? mediaLabel(r.tier) : KIND_LABEL[r.kind] || "Chat");

function Stat({ label, value, sub, testId }) {
  return (
    <div className="rounded-xl border border-white/[0.08] bg-ink-850 p-5" data-testid={testId}>
      <div className="text-xs text-zinc-500">{label}</div>
      <div className="mt-2 text-2xl font-semibold tracking-tight text-white">{value}</div>
      {sub && <div className="mt-1 text-xs text-zinc-500">{sub}</div>}
    </div>
  );
}

export default function PlanUsagePage() {
  const { data, isLoading, isError, error, refetch } = useUsage();
  const credits = data?.credits;

  return (
    <div className="h-full overflow-y-auto" data-testid="plan-usage-page">
      <div className="mx-auto w-full max-w-[1040px] px-4 pb-16 pt-8 sm:px-6 sm:pt-12">
        <h1 className="text-[1.7rem] font-semibold tracking-tight text-white sm:text-3xl">Credits & Usage</h1>
        <p className="mt-1 text-sm text-zinc-500">Credits are ChatSleek's usage unit. Different capabilities use different amounts.</p>
        {isError && (
          <div className="mt-8 rounded-xl border border-white/10 p-4 text-sm text-zinc-300" data-testid="usage-error">
            {error.message} <button type="button" className="underline underline-offset-4" onClick={() => refetch()}>Try again</button>
          </div>
        )}
        {isLoading && <div className="mt-8 h-40 animate-pulse rounded-xl bg-white/[0.04]" />}
        {data && (
          <>
            <div className="mt-8 rounded-2xl border border-white/[0.08] bg-ink-850 p-6" data-testid="usage-current-plan">
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.16em] text-zinc-500">Prepaid credits</div>
                  <div className="mt-2 text-sm text-zinc-400" data-testid="usage-plan-name">Buy a pack whenever you need more.</div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-semibold tracking-tight text-white" data-testid="usage-credits-remaining">
                    {formatNumber(credits.balance)}
                  </div>
                  <div className="text-xs text-zinc-500">credits remaining</div>
                </div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Stat label="Credits used this month" value={formatNumber(credits.used)} testId="usage-credits-used" />
              <Stat label="Requests this month" value={formatNumber(credits.requests)} testId="usage-requests" />
              <Stat label="Billing" value="Prepaid" sub="Pay only for what you use" testId="usage-billing-model" />
            </div>
            <section className="mt-10">
              <h2 className="mb-4 text-base font-medium text-zinc-100 md:text-lg">Last 30 days</h2>
              <div className="rounded-2xl border border-white/[0.08] bg-ink-850 p-5">
                <UsageChart daily={data.daily} />
              </div>
            </section>
            <section className="mt-10">
              <h2 className="mb-4 text-base font-medium text-zinc-100 md:text-lg">Recent activity</h2>
              <div className="overflow-hidden rounded-2xl border border-white/[0.08]" data-testid="usage-recent-activity">
                {data.recent.length === 0 && <div className="p-6 text-sm text-zinc-500" data-testid="usage-recent-empty">No activity yet. Your usage will appear here.</div>}
                {data.recent.map((r) => (
                  <div key={r.id} className="flex items-center gap-4 border-b border-white/[0.05] px-5 py-3 text-sm last:border-0" data-testid="usage-activity-row">
                    <span className="w-28 shrink-0 text-xs text-zinc-500">{formatDateTime(r.created_at)}</span>
                    <span className="min-w-0 flex-1 truncate text-zinc-200">
                      {r.kind === "task_parse" ? (
                        <span className="text-zinc-300">Task quick add</span>
                      ) : r.capability === "media" ? (
                        <Link to="/create" className="text-zinc-300 hover:underline">Create Studio</Link>
                      ) : r.conversation_id && r.conversation_title ? (
                        <Link to={`/chat/${r.conversation_id}`} className="hover:underline">{r.conversation_title}</Link>
                      ) : (
                        <span className="text-zinc-500">Deleted conversation</span>
                      )}
                      <span className="ml-2 text-xs text-zinc-600" data-testid="usage-activity-kind">
                        {activityLabel(r)} · {r.capability === "media" && r.outcome === "complete" ? "Completed" : OUTCOME_LABEL[r.outcome]}
                      </span>
                    </span>
                    <span className="shrink-0 tabular-nums text-zinc-300">{r.credits_charged ? `−${r.credits_charged}` : "0"}</span>
                  </div>
                ))}
              </div>
            </section>
            <section className="mt-10">
              <h2 className="mb-4 text-base font-medium text-zinc-100 md:text-lg">Buy credits</h2>
              <CreditPacks />
            </section>
          </>
        )}
      </div>
    </div>
  );
}
